/**
 * Resolves an asset URL that may be a root-relative path (e.g. `/tutorials/images/...`)
 * against the configured API base URL (`VITE_API_URL`).
 *
 * URLs that are already absolute (http/https/data) or protocol-relative (`//host/...`)
 * are preserved as-is.
 */
export function resolveApiAssetUrl(
  src: string | null | undefined,
  apiBaseUrl: string | undefined = import.meta.env.VITE_API_URL
): string {
  if (!src) return '';
  const api = (apiBaseUrl ?? '').replace(/\/$/, '');
  const isRootRelative = src.startsWith('/') && !src.startsWith('//');
  return isRootRelative && api ? `${api}${src}` : src;
}

const PUBLIC_ASSET_ORIGIN = 'https://innexpublic.blob.core.windows.net';
const SAS_SIGNATURE_PARAM = 'sig';

interface TrustedImageSources {
  apiBaseUrl?: string;
  /**
   * The Azure storage account the API signs (SAS) URLs for. Unset trusts no
   * signed blob host: a SAS URL proves only that *some* account signed it, and
   * anyone can mint one on their own account.
   */
  storageAccount?: string;
}

/**
 * Small, curated allowlist of third‑party origins we intentionally trust for
 * inline images in markdown. Keep this aligned with the frontend CSP
 * `img-src` directive in `public/staticwebapp.config.json`.
 *
 * Prefer origin strings (scheme + host, no trailing slash).
 */
const THIRD_PARTY_TRUSTED_IMAGE_ORIGINS: readonly string[] = (() => {
  // Allow overriding via env for emergency fixes without a code change.
  // Comma-separated list of origins, e.g. "https://export.supademo.com,https://cdn.loom.com"
  const fromEnv = (import.meta.env.VITE_TRUSTED_IMAGE_ORIGINS as string | undefined)
    ?.split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (fromEnv && fromEnv.length > 0) return fromEnv;
  return ['https://export.supademo.com', 'https://app.supademo.com', 'https://cdn.loom.com'] as const;
})();

/**
 * Whether an image URL from rendered markdown may be fetched automatically.
 *
 * Markdown can come from an LLM or a stored document, so loading an arbitrary
 * image would leak the reader's IP and any data encoded in the URL to its host
 * without a click. Trusted: the app's own origin, the API origin, the public
 * asset container, and signed (SAS) blob URLs on the configured storage
 * account. Relative URLs resolve against the current page. Select third‑party
 * origins used by the product are also allowed.
 */
export function isTrustedImageUrl(
  src: string,
  {
    apiBaseUrl = import.meta.env.VITE_API_URL,
    storageAccount = import.meta.env.VITE_STORAGE_ACCOUNT
  }: TrustedImageSources = {}
): boolean {
  let url: URL;
  try {
    url = new URL(src, window.location.href);
  } catch {
    return false;
  }
  const trustedOrigins = [
    window.location.origin,
    PUBLIC_ASSET_ORIGIN,
    originOf(apiBaseUrl),
    ...THIRD_PARTY_TRUSTED_IMAGE_ORIGINS
  ];
  if (trustedOrigins.includes(url.origin)) return true;
  return (
    !!storageAccount &&
    url.origin === `https://${storageAccount}.blob.core.windows.net` &&
    url.searchParams.has(SAS_SIGNATURE_PARAM)
  );
}

function originOf(base: string | undefined): string | undefined {
  try {
    return base ? new URL(base).origin : undefined;
  } catch {
    return undefined;
  }
}
